import { useEffect, useRef, type ReactNode } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The Gig Board's pop-up. A `locked` one (the Friday check-in) must be answered: no Escape, no tapping outside, and
 * no close button. Focus moves in on open and Tab stays inside.
 */
export function GigModal({
  label,
  locked = false,
  onClose,
  children,
}: {
  label: string;
  locked?: boolean;
  onClose?: () => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const first = box.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? box.current)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!locked) closeRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !box.current) return;
      const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) return;
      const firstItem = items[0]!;
      const lastItem = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      } else if (!box.current.contains(document.activeElement)) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [locked]);

  return (
    <div
      data-testid="modal-backdrop"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[#1d1214]/60 p-0 sm:items-center sm:p-4"
      onClick={(event) => {
        if (!locked && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="max-h-[92vh] w-full max-w-[560px] overflow-y-auto rounded-t-[var(--radius-card)] bg-surface p-5 outline-none sm:rounded-[var(--radius-card)]"
      >
        {children}
      </div>
    </div>
  );
}

export function ModalKicker({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
      {children}
    </div>
  );
}

export function ModalTitle({ children }: { children: ReactNode }) {
  return <h2 className="mb-2 text-[22px] leading-[1.2] text-accent">{children}</h2>;
}
